import { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, ImageUp, Video, Film, Undo, Redo } from "lucide-react";
import { uploadImage, uploadFile } from "../lib/api";
import { toast } from "sonner";

const Btn = ({ onClick, title, children, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
    data-testid={testid}
    className="w-9 h-9 flex items-center justify-center rounded hover:bg-slate-200 text-slate-700 transition-colors">
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("URL du lien :", "https://");
    if (url) cmd("createLink", url);
  };
  const addImage = () => {
    const url = window.prompt("URL de l'image :", "https://");
    if (url) cmd("insertImage", url);
  };
  const addVideo = () => {
    const url = window.prompt("URL YouTube (embed) ou lien vidéo :", "https://www.youtube.com/embed/");
    if (url) {
      cmd("insertHTML", `<iframe src="${url}" allowfullscreen></iframe><p><br/></p>`);
    }
  };
  const onUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    ref.current.focus();
    try {
      for (const f of files) {
        const url = await uploadImage(f);
        cmd("insertImage", url);
      }
      toast.success(`${files.length} image(s) insérée(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    }
    e.target.value = "";
  };
  const onUploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    ref.current.focus();
    toast.info("Téléversement de la vidéo en cours…");
    try {
      const url = await uploadFile(file);
      cmd("insertHTML", `<video controls src="${url}" style="width:100%"></video><p><br/></p>`);
      toast.success("Vidéo insérée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    }
    e.target.value = "";
  };

  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-[#E2E8F0] bg-slate-50 sticky top-0 z-10">
        <Btn onClick={() => cmd("bold")} title="Gras" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => cmd("italic")} title="Italique" testid="rte-italic"><Italic size={16} /></Btn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("formatBlock", "<h2>")} title="Titre" testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<h3>")} title="Sous-titre" testid="rte-h3"><Heading3 size={16} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<blockquote>")} title="Citation" testid="rte-quote"><Quote size={16} /></Btn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("insertUnorderedList")} title="Liste à puces" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} title="Liste numérotée" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={addLink} title="Lien" testid="rte-link"><LinkIcon size={16} /></Btn>
        <Btn onClick={addImage} title="Image par URL" testid="rte-image"><ImageIcon size={16} /></Btn>
        <Btn onClick={() => fileRef.current?.click()} title="Téléverser des images (plusieurs possible)" testid="rte-upload"><ImageUp size={16} /></Btn>
        <Btn onClick={addVideo} title="Vidéo par URL" testid="rte-video"><Video size={16} /></Btn>
        <Btn onClick={() => videoFileRef.current?.click()} title="Téléverser une vidéo" testid="rte-upload-video"><Film size={16} /></Btn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("formatBlock", "<p>")} title="Paragraphe" testid="rte-p"><span className="text-xs font-bold">¶</span></Btn>
        <Btn onClick={() => cmd("undo")} title="Annuler" testid="rte-undo"><Undo size={16} /></Btn>
        <Btn onClick={() => cmd("redo")} title="Rétablir" testid="rte-redo"><Redo size={16} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        data-testid="rte-content"
        data-placeholder="Rédigez votre article ici…"
        onInput={emit}
        className="rte-content p-4"
        suppressContentEditableWarning
      />
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="rte-upload-input" onChange={onUploadFile} />
      <input ref={videoFileRef} type="file" accept="video/*" className="hidden" data-testid="rte-upload-video-input" onChange={onUploadVideo} />
    </div>
  );
}
